import { FC } from 'react';
import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';
import { Button, Col, Container, Row } from 'react-bootstrap';
import { PersonCircle } from 'react-bootstrap-icons';
import { useFormContext, Controller } from 'react-hook-form';

import DatePicker from 'react-datepicker';
import { ErrorMessage } from '@hookform/error-message';

import 'react-datepicker/dist/react-datepicker.css';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

interface UserDetailsModalProps {
  show: boolean;
  handleClose: () => void;
  isEnd?: React.Dispatch<React.SetStateAction<boolean>>;
}

export const UserDetailsModal: FC<UserDetailsModalProps> = ({
  show,
  handleClose,
  isEnd,
}) => {
  const {
    control,
    register,
    trigger,
    formState: { errors },
  } = useFormContext();

  const onClickSubmit = async () => {
    const validated = await trigger([
      'firstName',
      'lastName',
      'email',
      'phoneNumber',
      'birthDate',
      'licensePlate',
    ]);

    // TODO: submit user details
    if (isEnd && validated) {
      isEnd(true);
      handleClose();
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="lg"
      dialogClassName="post-editor-modal modal-200w"
      backdrop="static"
      id="guest-portal"
    >
      <Modal.Header className="guest-modal-header ">
        <Modal.Title className="editor-title">
          <PersonCircle size={25} /> {'  '}
          Enter your Details
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="guest-modal-header">
        <Form>
          <Row>
            <Col>
              <Form.Label className="editor-title-label">First Name</Form.Label>
              <Form.Control
                className="editor-control guest-inputs"
                placeholder="Enter your First name..."
                {...register('firstName', { required: 'This is required.' })}
              />
              <ErrorMessage
                errors={errors}
                name="firstName"
                render={({ message }) => <span className="error-text">{message}</span>}
              />
            </Col>
          </Row>
          <Row className="mt-16px">
            <Col>
              <Form.Label className="editor-title-label">Last Name</Form.Label>
              <Form.Control
                className="editor-control guest-inputs"
                placeholder="Enter your Last name..."
                {...register('lastName', { required: 'This is required.' })}
              />
              <ErrorMessage
                errors={errors}
                name="lastName"
                render={({ message }) => <span className="error-text">{message}</span>}
              />
            </Col>
          </Row>

          <Row className="mt-16px">
            <Col>
              <Form.Label className="editor-title-label">Email Address</Form.Label>
              <Form.Control
                className="editor-control guest-inputs"
                placeholder="Enter your Email..."
                {...register('email', {
                  required: 'This is required.',
                  pattern: {
                    value: /\S+@\S+\.\S+/,
                    message: 'Entered value does not match email format',
                  },
                })}
              />
              <ErrorMessage
                errors={errors}
                name="email"
                render={({ message }) => <span className="error-text">{message}</span>}
              />
            </Col>
          </Row>
          <Row className="mt-16px">
            <Col>
              <Form.Label className="editor-title-label">Phone number</Form.Label>

              <Controller
                name="phoneNumber"
                control={control}
                rules={{
                  required: 'This is required.',
                  validate: {
                    hasMoreThanFourDigits: value => {
                      const digits = value.replace(/\D/g, '');
                      return digits.length > 4 || 'Enter a valid number.';
                    },
                  },
                }}
                render={({ field: { onChange, value } }) => (
                  <PhoneInput
                    country="us"
                    value={value}
                    onChange={phoneValue => {
                      onChange(
                        phoneValue.startsWith('+') ? phoneValue : `+${phoneValue}`,
                      );
                    }}
                    inputStyle={{ width: '100%' }}
                    countryCodeEditable={false}
                  />
                )}
              />

              <ErrorMessage
                errors={errors}
                name="phoneNumber"
                render={({ message }) => <span className="error-text">{message}</span>}
              />
            </Col>
          </Row>

          <Row className="mt-16px">
            <Col xs={12}>
              <Form.Label className="editor-title-label">Date of Birth</Form.Label>
            </Col>
            <Col xs={12}>
              <div className="datePicker">
                <Controller
                  control={control}
                  name="birthDate"
                  rules={{ required: 'This is required.' }}
                  render={({ field }) => (
                    <DatePicker
                      {...field}
                      wrapperClassName="datePicker"
                      placeholderText="Select date"
                      onChange={date => field.onChange(date)}
                      selected={field.value}
                      customInput={
                        <Form.Control className="editor-control guest-inputs" />
                      }
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="select"
                      maxDate={new Date()}
                    />
                  )}
                />
              </div>
              <ErrorMessage
                errors={errors}
                name="birthDate"
                render={({ message }) => <span className="error-text">{message}</span>}
              />
            </Col>
          </Row>

          <Row className="mt-16px">
            <Col>
              <Form.Label className="editor-title-label">License Plate</Form.Label>
              <Form.Control
                className="editor-control guest-inputs"
                placeholder="Enter your License plate..."
                {...register('licensePlate')}
              />
              <ErrorMessage
                errors={errors}
                render={({ message }) => <span className="error-text">{message}</span>}
                name="licensePlate"
              />
            </Col>
          </Row>
        </Form>
      </Modal.Body>
      <Container className="guest-modal-footer ">
        <Button className="cancel-button no-border-radius" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          className="save-button no-border-radius"
          style={{ marginLeft: '10px' }}
          onClick={onClickSubmit}
        >
          Continue
        </Button>
      </Container>
    </Modal>
  );
};
